import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { config } from '../config';

export interface GodSystemOverview {
  recruiting_companies: number;
  marketing_companies: number;
  total_users: number;
  total_contacts: number;
  total_templates: number;
  system_health: SystemHealth;
  recent_activities: Activity[];
  isolation_status: IsolationStatus;
}

export interface RCMCHierarchy {
  rc: {
    id: string;
    name: string;
    status: string;
    user_count: number;
    created_at: Date;
  };
  marketing_companies: MCDetails[];
  total_mc_users: number;
  hierarchy_health: 'HEALTHY' | 'WARNINGS' | 'ISSUES';
}

export interface MCDetails {
  id: string;
  name: string;
  status: string;
  user_count: number;
  contact_count: number;
  template_count: number;
  last_activity?: Date;
  isolation_score: number;
}

export interface SystemHealth {
  database: 'HEALTHY' | 'DEGRADED' | 'OFFLINE';
  isolation: 'SECURE' | 'WARNINGS' | 'VIOLATIONS';
  hierarchy: 'STABLE' | 'ISSUES' | 'BROKEN';
  overall: 'HEALTHY' | 'WARNINGS' | 'CRITICAL';
  uptime_hours: number;
}

export interface IsolationStatus {
  total_violations_24h: number;
  critical_violations_24h: number;
  companies_affected: number;
  last_violation?: Date;
  status: 'SECURE' | 'MONITORING' | 'ALERT' | 'CRITICAL';
}

export interface Activity {
  id: string;
  timestamp: Date;
  type: 'USER_ACTION' | 'SYSTEM_EVENT' | 'ISOLATION_VIOLATION' | 'RC_MC_CHANGE';
  description: string;
  userId?: string;
  companyId?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface IsolationReport {
  totalViolations: number;
  violationsByRole: Record<string, number>;
  violationsByCompany: Record<string, number>;
  recentViolations: any[];
  systemHealth: 'HEALTHY' | 'WARNINGS' | 'CRITICAL';
}

export interface CompanyDetails {
  company: {
    id: string;
    name: string;
    description?: string;
    status: string;
    company_type: 'RC' | 'MC';
    parent_recruiting_company?: string;
    active: boolean;
    created_at: Date;
    updated_at: Date;
  };
  users: any[];
  user_count: number;
  access_patterns: any;
  recent_violations: any[];
  isolation_score: number;
}

export interface CreateCompanyRequest {
  name: string;
  description?: string;
  status?: string;
  company_type?: 'RC' | 'MC';
  parent_recruiting_company?: string;
}

export interface AssignMCToRCRequest {
  mcCompanyId: string;
  rcCompanyId: string;
}

export interface CompanyDropdownOption {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class GodService {
  private apiUrl = config.apiUrl;

  constructor(private http: HttpClient) {}

  // Enhanced dashboard overview with RC-MC hierarchy
  getDashboardOverview(): Observable<GodSystemOverview> {
    return this.http.get<GodSystemOverview>(`${this.apiUrl}/api/admin/god/system-overview`);
  }

  // Get RC-MC hierarchy view
  getRCMCHierarchy(): Observable<RCMCHierarchy[]> {
    return this.http.get<RCMCHierarchy[]>(`${this.apiUrl}/api/admin/god/hierarchy-data`);
  }

  // Get isolation violation report
  getIsolationReport(days: number = 7): Observable<IsolationReport> {
    return this.http.get<IsolationReport>(`${this.apiUrl}/api/admin/god/isolation-report?days=${days}`);
  }

  // Get enhanced company details with God-level access
  getCompanyDetails(companyId: string): Observable<CompanyDetails> {
    return this.http.get<CompanyDetails>(`${this.apiUrl}/api/admin/god/companies/${companyId}/enhanced`);
  }

  // Create a new company with RC/MC type support
  createCompany(companyData: CreateCompanyRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/admin/god/companies`, companyData);
  }

  // Assign MC to RC
  assignMCToRC(assignData: AssignMCToRCRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/admin/god/assign-mc-to-rc`, assignData);
  }

  // Get all companies for management
  getAllCompanies(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/api/admin/god/companies`);
  }

  // Get all users across all companies
  getAllUsers(page: number = 1, perPage: number = 50, search?: string): Observable<any> {
    let params: any = { page: page.toString(), perPage: perPage.toString() };
    if (search) {
      params.search = search;
    }
    return this.http.get<any>(`${this.apiUrl}/api/admin/god/users`, { params });
  }

  // Get system health information
  getSystemHealth(): Observable<SystemHealth> {
    return this.http.get<SystemHealth>(`${this.apiUrl}/api/admin/god/system-health`);
  }

  // Legacy method - get system stats
  getSystemStats(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/admin/god/stats`);
  }

  // Legacy method - get companies overview
  getCompaniesOverview(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/admin/god/companies-overview`);
  }

  // Get RC companies for dropdown (for forms)
  getRCCompaniesDropdown(): Observable<CompanyDropdownOption[]> {
    return this.http.get<CompanyDropdownOption[]>(`${this.apiUrl}/api/admin/god/rc-companies-dropdown`);
  }

  // Get MC companies under specific RC for dropdown (for forms)
  getMCCompaniesForRCDropdown(rcId: string): Observable<CompanyDropdownOption[]> {
    return this.http.get<CompanyDropdownOption[]>(`${this.apiUrl}/api/admin/god/mc-companies-dropdown/${rcId}`);
  }
}